// 261015 P4c-1 (D5) — công tắc "Tên truyện / Tên phim" của tổng quan. THUẦN (jest).
//
// Tổng quan LƯU tên/trích NGUYÊN TÁC (không thay tên đệ quy vào JSON — mất bằng chứng trích). Công tắc chỉ đổi cách HIỂN THỊ:
//  - tiêu đề nhân vật tra theo `characterId` → `localizedName` của khung xương;
//  - văn xuôi của ý (`text`) và ghi chú biên kịch đi qua bảng thay tên dựng từ khung xương (tên nguyên tác + bí danh → tên phim).
//  - 🔴 TRÍCH NGUYÊN VĂN (`quote`) KHÔNG BAO GIỜ bị thay tên — đó là bằng chứng.
// FE chưa có bảng glossary/`swapPairs` của BE; bảng ở đây chỉ gồm nhân vật có `localizedName` (nói rõ trong báo cáo).
import type { OutlineCharacter, OverviewProfile } from '../../../../types/adaptation';

export type NameMode = 'story' | 'film';
export interface NamePair { from: string; to: string }

const norm = (s: string): string => String(s ?? '').trim().replace(/\s+/g, ' ');
const sameName = (a: string, b: string): boolean => norm(a).toLocaleLowerCase('vi') === norm(b).toLocaleLowerCase('vi');

/** Tên phim của nhân vật: `localizedName` nếu có và khác tên gốc; ngược lại rỗng (= chưa đổi tên). */
export const filmNameOfCharacter = (c: Pick<OutlineCharacter, 'name' | 'localizedName'> | null | undefined): string => {
  const ln = norm(c?.localizedName ?? '');
  return ln && c && !sameName(ln, c.name) ? ln : '';
};

/**
 * Bảng thay tên: mỗi nhân vật có tên phim ⇒ {tên gốc, bí danh (`aliases`, `sourceAliases`)} → tên phim. Dài trước ngắn (tên đầy đủ thắng bí danh ngắn), hoà ⇒ theo chữ.
 * Tên < 2 ký tự bị bỏ (thay "A" khắp văn bản là phá chữ). Hai nhân vật trùng một tên gốc ⇒ không thay (mơ hồ): bỏ cả hai.
 */
export const buildNamePairs = (characters: ReadonlyArray<OutlineCharacter> | null | undefined): NamePair[] => {
  const byFrom = new Map<string, Set<string>>();
  for (const c of characters ?? []) {
    const to = filmNameOfCharacter(c);
    if (!to) continue;
    for (const raw of [c.name, ...(c.aliases ?? []), ...(c.sourceAliases ?? [])]) {
      const from = norm(raw);
      if (from.length < 2 || sameName(from, to)) continue;
      const key = from.toLocaleLowerCase('vi');
      if (!byFrom.has(key)) byFrom.set(key, new Set());
      (byFrom.get(key) as Set<string>).add(`${from}\u0000${to}`);
    }
  }
  const out: NamePair[] = [];
  Array.from(byFrom.values()).forEach((set) => {
    if (set.size !== 1) return;
    const [from, to] = Array.from(set)[0].split('\u0000');
    out.push({ from, to });
  });
  return out.sort((a, b) => b.from.length - a.from.length || (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
};

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Thay tên trong văn xuôi — MỘT lượt (chuỗi đã thay không bị thay lần nữa), biên từ Unicode (không thay trong từ dài hơn), không phân biệt hoa/thường ở tên gốc. */
export const swapNames = (text: string, pairs: ReadonlyArray<NamePair>): string => {
  const s = String(text ?? '');
  if (!s || !pairs.length) return s;
  const map = new Map(pairs.map((p) => [p.from.toLocaleLowerCase('vi'), p.to] as const));
  const alt = pairs.map((p) => escapeRe(p.from)).join('|');
  const re = new RegExp(`(?<![\\p{L}\\p{M}\\p{N}_])(?:${alt})(?![\\p{L}\\p{M}\\p{N}_])`, 'giu');
  return s.replace(re, (m) => map.get(m.toLocaleLowerCase('vi')) ?? m);
};

export interface ProfileTitle { title: string; sub: string }

/**
 * Tiêu đề hồ sơ. `story` ⇒ tên nguyên tác. `film` ⇒ tên phim của nhân vật được ánh xạ (tra theo id); chưa có tên phim / chưa ánh xạ ⇒ rơi về tên nguyên tác (không bịa).
 * `sub` = tên còn lại để người đọc đối chiếu ("truyện: Ôn Noãn" / "phim: Nora Wen"); rỗng khi trùng.
 */
export const profileTitle = (p: Pick<OverviewProfile, 'sourceName' | 'characterId'>, characters: ReadonlyArray<OutlineCharacter> | null | undefined, mode: NameMode): ProfileTitle => {
  const ch = p.characterId ? (characters ?? []).find((c) => c.id === p.characterId) : undefined;
  const film = filmNameOfCharacter(ch);
  if (mode === 'film' && film) return { title: film, sub: sameName(film, p.sourceName) ? '' : `truyện: ${p.sourceName}` };
  return { title: p.sourceName, sub: film && !sameName(film, p.sourceName) ? `phim: ${film}` : '' };
};
